import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface VerifiedCompany {
  id: number;
  name: string;
  image_url: string | null;
  website_url: string | null;
  category: string | null;
}

const categoryIcons: Record<string, string> = {
  카지노: "ri-vip-diamond-line",
  슬롯: "ri-gamepad-line",
  토토: "ri-football-line",
  바카라: "ri-magic-line",
  홀덤: "ri-gamepad-line",
  종합: "ri-building-4-line",
};

export default function QuickLinks() {
  const [companies, setCompanies] = useState<VerifiedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [visible, setVisible] = useState(false);
  const INITIAL_SHOW = 4;

  useEffect(() => {
    let cancelled = false;

    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(false);
        const { data, error: fetchError } = await supabase
          .from("verified_companies")
          .select("id, name, image_url, website_url, category")
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
          .order("sort_order", { ascending: true });

        if (cancelled) return;
        if (fetchError) throw fetchError;

        setCompanies(data || []);
        setTimeout(() => { if (!cancelled) setVisible(true); }, 50);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCompanies();
    return () => { cancelled = true; };
  }, [refetchTrigger]);

  const handleRetry = () => {
    setVisible(false);
    setRefetchTrigger((prev) => prev + 1);
  };

  return (
    <div className="bg-background-100 rounded-lg border border-background-500 p-3 md:p-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2">
          <span className="relative w-7 h-7 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-primary-500/20 animate-ping opacity-30" />
            <span className="relative w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs md:text-sm z-10">
              <i className="ri-shield-check-line" />
            </span>
          </span>
          <div>
            <h3 className="text-xs md:text-sm font-bold text-foreground-950">보증업체</h3>
            {!loading && companies.length > 0 && (
              <p className="text-[9px] md:text-[10px] text-foreground-600">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-500 mr-1 align-middle" />
                {companies.length}개 업체 인증완료
              </p>
            )}
          </div>
        </div>
        <Link
          to="/verified"
          className="group flex items-center gap-1 text-[10px] md:text-xs text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          전체보기
          <span className="w-3 h-3 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-0.5">
            <i className="ri-arrow-right-s-line" />
          </span>
        </Link>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="relative aspect-[3/2] rounded-xl overflow-hidden bg-background-200">
              <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent animate-pulse" />
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <div className="h-2 w-3/4 mx-auto rounded-full bg-background-300 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-6 md:py-8 text-center">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500/10 mb-2">
            <i className="ri-error-warning-line text-lg text-red-500" />
          </span>
          <p className="text-xs md:text-sm text-foreground-700 font-medium">데이터를 불러오지 못했습니다</p>
          <p className="text-[10px] text-foreground-600 mt-0.5 mb-3">잠시 후 다시 시도해주세요</p>
          <button
            onClick={handleRetry}
            className="px-4 py-1.5 text-[11px] font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />
            다시 시도
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && companies.length === 0 && (
        <div className="flex flex-col items-center justify-center py-6 md:py-8 text-center">
          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 mb-2">
            <i className="ri-store-2-line text-lg text-foreground-600" />
          </span>
          <p className="text-xs md:text-sm text-foreground-700 font-medium">등록된 보증업체가 없습니다</p>
          <p className="text-[10px] text-foreground-600 mt-0.5">새로운 업체를 기다려주세요</p>
        </div>
      )}

      {/* Cards */}
      {!loading && !error && companies.length > 0 && (
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          {companies.slice(0, INITIAL_SHOW).map((company, idx) => {
            const iconClass = company.category ? categoryIcons[company.category] || "ri-building-line" : "ri-building-line";

            return (
              <Link
                key={company.id}
                to={`/verified/${company.id}`}
                className="group relative aspect-[3/2] rounded-xl overflow-hidden border border-background-300 bg-background-200 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/10 hover:border-primary-300 hover:scale-[1.04]"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0) scale(1)" : "translateY(8px) scale(0.96)",
                  transition: `opacity 0.4s ease ${idx * 0.08}s, transform 0.4s ease ${idx * 0.08}s, box-shadow 0.25s ease, border-color 0.25s ease`,
                }}
              >
                {/* Glow ring on hover */}
                <span className="absolute -inset-0.5 rounded-xl bg-gradient-to-br from-primary-500/0 via-primary-500/0 to-accent-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 group-hover:from-primary-500/20 group-hover:via-transparent group-hover:to-accent-500/20 -z-10 blur-sm" />

                {/* Image or fallback icon */}
                {company.image_url ? (
                  <img
                    src={company.image_url}
                    alt={company.name}
                    className="w-full h-full object-contain px-2 md:px-3 py-0 md:py-0.5 transition-transform duration-300 group-hover:scale-95"
                  />
                ) : (
                  <span className="w-full h-full flex items-center justify-center text-foreground-600 transition-colors duration-300 group-hover:text-primary-400">
                    <i className={`${iconClass} text-2xl md:text-3xl`} />
                  </span>
                )}

                {/* Name overlay on hover */}
                <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
                  <div className="bg-gradient-to-t from-background-950/90 via-background-950/70 to-transparent px-2 py-3 pt-5">
                    <div className="flex items-center gap-1 justify-center mb-1">
                      <span className="w-3 h-3 flex items-center justify-center text-primary-400">
                        <i className={iconClass} />
                      </span>
                      <span className="text-[9px] md:text-[10px] font-bold text-white truncate max-w-full">
                        {company.name}
                      </span>
                    </div>
                    {company.category && (
                      <span className="block text-center text-[7px] md:text-[8px] text-primary-300 bg-primary-500/15 rounded-full px-2 py-0.5 mx-auto w-fit">
                        {company.category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Verified badge */}
                <span className="absolute top-1.5 right-1.5 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full bg-primary-500 text-white shadow-sm shadow-primary-500/30 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                  <i className="ri-check-fill text-[8px] md:text-[10px]" />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}